import React, { useContext, useState } from "react";
import { observer } from "mobx-react";
import { ArrowRight, Bot, Check, FileText, Link } from "lucide-react";
import { AppWorkspaceContext } from "../stores/AppWorkspace";
import { Button } from "../../ui";
import { copyFetchedText, copyText } from "../../copyToClipboard";

// Same paths as ApiDocsMarkdown::COVER_PATH / FULL_PATH.
const COVER_PATH = "/api_docs.md";
const FULL_PATH = "/api_docs/full.md";

type CopyKind = "link" | "text";

// "Agent directions": hands a coding agent the API docs. Copy link puts the
// URL of the short cover (which tells the agent to download the full
// reference) on the clipboard; Copy full text puts the full reference itself.
// /llms.txt lists both for agents that look there first. The same section, in
// ERB, opens /api_docs.
const AgentDirections: React.FC = observer(() => {
  const workspace = useContext(AppWorkspaceContext);
  const [copied, setCopied] = useState<CopyKind | null>(null);
  const coverUrl = `${window.location.origin}${COVER_PATH}`;

  const copy = async (kind: CopyKind) => {
    try {
      await (kind === "link" ? copyText(coverUrl) : copyFetchedText(FULL_PATH));
      setCopied(kind);
      window.setTimeout(() => setCopied(null), 1500);
    } catch {
      setCopied(null);
    }
  };

  return (
    <section className="agent-directions" aria-labelledby="agent-directions-title">
      <div className="agent-directions-eyebrow">
        <span className="agent-directions-eyebrow-icon">
          <Bot size={16} />
        </span>
        For developers
      </div>
      <h2 id="agent-directions-title" className="agent-directions-title">
        Have your agent add Lightning Swap to your app
      </h2>
      <p className="agent-directions-body">
        Copy these directions into Claude Code, Codex, Cursor or any coding agent. They are the
        whole API — signed requests, quotes, orders and refunds — so it can build the integration
        for you. It will need an API key, which you create{" "}
        {workspace.isGuest ? (
          <>
            after you <a href="/login">sign in</a>
          </>
        ) : (
          <>
            on the <a href="/?page=api_tokens">API keys</a> page
          </>
        )}
        .
      </p>

      <div className="agent-directions-link">
        <span className="agent-directions-url">{coverUrl}</span>
        <Button
          size="sm"
          variant={copied === "link" ? "accent" : "secondary"}
          className="shrink-0"
          leftIcon={copied === "link" ? <Check size={16} /> : <Link size={16} />}
          title={`Copy a link to the agent directions at ${COVER_PATH}`}
          onClick={() => void copy("link")}
        >
          {copied === "link" ? "Link copied" : "Copy link"}
        </Button>
      </div>

      <div className="agent-directions-actions">
        <Button
          size="sm"
          variant="ghost"
          leftIcon={copied === "text" ? <Check size={16} /> : <FileText size={16} />}
          title="Copy the full API reference as text, for an agent that cannot fetch a URL"
          onClick={() => void copy("text")}
        >
          {copied === "text" ? "Text copied" : "Copy full text"}
        </Button>
        <a href="/api_docs" className="agent-directions-more">
          API docs
          <ArrowRight size={14} />
        </a>
        <a href="/llms.txt" className="agent-directions-more">
          llms.txt
          <ArrowRight size={14} />
        </a>
      </div>
    </section>
  );
});

export default AgentDirections;
